import { createRouter, RouterProvider } from "@tanstack/react-router"
import { StrictMode } from "react"
import ReactDOM from "react-dom/client"
import * as TanStackQueryProvider from "@/app/providers/tanstack-query/root-provider.tsx"

import "@medme/ui/styles/global.css"
import "./config/i18n.ts"
import "@/app/config/route-static-data"
import type { AuthState } from "@/entities/auth"
import reportWebVitals from "./config/report-web-vitals.ts"
// Import the generated route tree
import { routeTree } from "./config/route-tree.gen"
import initSentry from "./config/sentry.ts"

// Create initial auth state
const initialAuthState: AuthState = {
	isAuthenticated: false,
	isAuthenticating: false,
	error: null,
	logout: async () => {
		window.location.reload()
	},
}

// Create a new router instance
const TanStackQueryProviderContext = TanStackQueryProvider.getContext()
const rootRouter = createRouter({
	routeTree,
	context: {
		...TanStackQueryProviderContext,
		auth: initialAuthState,
	},
	defaultPreload: "intent",
	scrollRestoration: true,
	defaultStructuralSharing: true,
	defaultPreloadStaleTime: 0,
})

// Register the router instance for type safety
declare module "@tanstack/react-router" {
	interface Register {
		router: typeof rootRouter
	}
}

async function enableMockingIfNeeded() {
	// Dynamic import keeps MSW + generated handlers out of the production bundle
	// when mocks are off (static import pulled ~all *.msw into the main chunk).
	if (import.meta.env.VITE_ENABLE_MOCK !== "true") {
		return
	}
	const { enableMocking } = await import("./mocks/browser.ts")
	await enableMocking()
}

function renderApp(rootElement: HTMLElement) {
	const root = ReactDOM.createRoot(rootElement)
	root.render(
		<StrictMode>
			<TanStackQueryProvider.Provider {...TanStackQueryProviderContext}>
				<RouterProvider router={rootRouter} />
			</TanStackQueryProvider.Provider>
		</StrictMode>,
	)
}

// Render the app
const rootElement = document.getElementById("app")
if (rootElement && !rootElement.innerHTML) {
	enableMockingIfNeeded().then(() => {
		renderApp(rootElement)
	})
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
initSentry(rootRouter)
reportWebVitals()
